import { useRef } from 'react';

/**
 * Whether the result still in hand answers the request being made.
 *
 * Apollo's `previousData` is the last *successful* result for whatever variables it was asked
 * with, so `data ?? previousData` keeps rows on screen through a transition — which is what
 * makes re-sorting or paging dim a table rather than empty it. After a *failure* it is doing
 * something else entirely: the rows in hand answer the cut before this one, and rendering them
 * says the new search, filter, sort or page returned them.
 *
 * The two cases a section has to tell apart:
 *
 *   • same cut, failed revalidation — the rows are still the answer; a mutation refetch that
 *     bounced should not empty a table the user is working in.
 *   • new cut that never answered — the rows answer a question the user has moved on from,
 *     and a table whose error row yields to rows will suppress the failure outright.
 *
 * Pass a key identifying the current cut (the query variables serialised is the honest one —
 * it cannot drift from what was actually sent) and whether `data` is present for it. Returns
 * true while the retained result belongs to a different cut, so the caller can hand back
 * nothing rather than the wrong answer.
 */
export default function useStaleCut(cut: string, hasCurrentData: boolean): boolean {
  const answered = useRef<string | null>(null);
  if (hasCurrentData) {
    answered.current = cut;
  }
  return !hasCurrentData && answered.current !== cut;
}
